import { createContext, useContext, useEffect, useState, ReactNode } from 'react'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:7071/api'

interface User {
  user_id: string
  email: string
  role: string
  name: string
  panels?: string[]
}

interface AuthContextType {
  user: User | null
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  getAccessToken: () => Promise<string | null>
  refreshPanels: () => Promise<void>
}

/** Where to send a logged-in user who lands on a route that doesn't care
 * which panel they use (the "/" index route, or a login page's post-login
 * redirect) -- picks their first available panel in a fixed priority order.
 * Missing/undefined `panels` is treated as having `vendedor`, for sessions
 * logged in before this field existed. */
export function getLandingRoute(user: User | null): string {
  if (!user) return '/login'
  // Admin manages more than one business from this portal (Café Saldaña,
  // FX Admin) -- land on the picker first instead of assuming which one.
  // Sellers/collaborators only ever work in the coffee business, so they
  // keep going straight to their panel below, unaffected.
  if (user.role === 'admin') return '/choose-business'
  const panels = user.panels
  if (!panels || panels.includes('vendedor')) return '/dashboard'
  if (panels.includes('logistica')) return '/logistica'
  if (panels.includes('rrhh')) return '/rrhh'
  if (panels.includes('finanzas')) return '/finanzas'
  return '/profile'
}

const PANEL_LABELS: Record<string, string> = {
  logistica: 'Logística',
  finanzas: 'Finanzas',
  rrhh: 'RRHH',
}

/** Human-facing Spanish label for a user's role, given the DB stores every
 * non-admin account as role `'seller'` regardless of which panels they
 * actually have access to. Missing/undefined `panels` is treated as having
 * `vendedor`, same backward-compatibility rule as `getLandingRoute`. */
export function getRoleLabel(role: string, panels?: string[]): string {
  if (role === 'admin') return 'Administrador'
  if (!panels || panels.includes('vendedor')) return 'Vendedor'
  const labels = panels.map((panel) => PANEL_LABELS[panel]).filter(Boolean)
  return labels.length > 0 ? `Colaborador — ${labels.join('/')}` : 'Colaborador'
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const token = localStorage.getItem('auth_token')
    const storedUser = localStorage.getItem('auth_user')
    if (token && storedUser) {
      try {
        setUser(JSON.parse(storedUser))
      } catch {
        localStorage.removeItem('auth_token')
        localStorage.removeItem('auth_user')
      }
    }
    setLoading(false)
  }, [])

  const login = async (email: string, password: string) => {
    const res = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    })

    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error(data.error || 'Error al iniciar sesión')
    }

    const { token, user: userData } = await res.json()
    localStorage.setItem('auth_token', token)
    localStorage.setItem('auth_user', JSON.stringify(userData))
    // sessionStorage (not localStorage) so this only fires once per actual
    // login -- it clears itself when the tab/browser closes, unlike a
    // session restored from localStorage on page refresh.
    sessionStorage.setItem('just_logged_in', '1')
    setUser(userData)
  }

  const logout = () => {
    localStorage.removeItem('auth_token')
    localStorage.removeItem('auth_user')
    setUser(null)
    window.location.href = '/login'
  }

  const getAccessToken = async (): Promise<string | null> => {
    return localStorage.getItem('auth_token')
  }

  /** Best-effort background revalidation of a collaborator's granted panels,
   * so a panel revoked mid-session stops appearing in the sidebar without
   * requiring a re-login. Silent on failure -- not a user-facing action. */
  const refreshPanels = async (): Promise<void> => {
    try {
      const res = await fetch(`${API_BASE_URL}/profile`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
      })
      if (!res.ok) return
      const profile = await res.json()
      setUser((prev) => {
        if (!prev) return prev
        const updated = { ...prev, panels: profile.panels }
        localStorage.setItem('auth_user', JSON.stringify(updated))
        return updated
      })
    } catch {
      // silent -- this is a best-effort background revalidation, not a
      // user-facing action; a failed refresh just means the sidebar stays
      // as it was until the next successful one
    }
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, getAccessToken, refreshPanels }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
